import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import { FC } from 'react';
import { Provider } from 'react-redux';
import OpportunityReviewBubbleChart, {
  OpportunityReviewBubbleChartProps,
} from '../../../data-visualization/opportunity-review-bubble-chart/OpportunityReviewBubbleChart';
import { BubbleChartResp } from '@models/OpportunityReview';
import { renderLoading } from '@pages/Loading';
import { getOpportunityReviewBubbleChart } from '@services/Chart.service';
import Store from '../../../state/Store';
import { isPeriodValueYTDOrMAT } from '@utils/PeriodUtils';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { useRegisterExport } from '@utils/collectionUtils/hooks/UseRegisterExport';
import { RelativePeriod } from '../../comparison-period-selector/Types';
import { useExportContext } from '@hooks/export/context/ExportContext';
import ExportContextProvider from '@hooks/export/context/ExportContextProvider';
import { getBubbleChartSelectedPeriod, useReRenderBubbleChart } from './BubbleChartInsightUtils';
import { OpportunityReviewBubbleChartInsight } from './Model';
import PgWarningBanner from '@components/collections/warning-banner/pg-warning-banner/PgWarningBanner';

enableChartsExporting();

type BubbleChartInsightRenderer = InsightRendererProps<OpportunityReviewBubbleChartInsight>;

const BubbleChartComponent: FC<BubbleChartInsightRenderer> = ({ insight, onLoaded, onExportReady }) => {
  const {
    apiParams,
    retailerType,
    growthType,
    factRevenue,
    volume,
    selectedBrandBubble,
    collectionDataType,
    isBubbleChartV2,
  } = insight.data;
  const { periodType, period, comparisonPeriod, saleType } = apiParams;
  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const isV3UsingBubbleChartV2YtdOrMat = isBubbleChartV2 && isPeriodValueYTDOrMAT(period);

      const bubbleChartApiParams = {
        ...apiParams,
        isUsingCollectionLiveData: collectionDataType === 'live' || isV3UsingBubbleChartV2YtdOrMat,
      };

      return getOpportunityReviewBubbleChart(bubbleChartApiParams);
    },
    onLoaded,
  });

  const { reRenderBubbleChart } = useReRenderBubbleChart();
  const { chartRefsIsInitialisedStatus } = useExportContext();
  const { multipleChartsExport } = useRegisterExport({
    numberOfChartsToExport: 1,
    onExportReady,
    chartRef: chartRefsIsInitialisedStatus,
    onExportAfter: reRenderBubbleChart,
  });

  const selectedPeriod = getBubbleChartSelectedPeriod(
    collectionDataType,
    periodType,
    period,
    comparisonPeriod as RelativePeriod,
    chartData,
  ).toUpperCase();

  const isSalesRecordsFound = chartData?.salesRecordsFound;
  const props: OpportunityReviewBubbleChartProps = {
    data: isSalesRecordsFound ? chartData : new BubbleChartResp(),
    retailerType: retailerType,
    selectedPeriod: selectedPeriod,
    selectedGrowthType: growthType,
    factRevenue: factRevenue,
    saleType: saleType,
    isDataChanged: false,
    isExportModalOpen: true, // this will hide the tooltips displayed for each corner of the chart
    volume: volume,
    setIsDataChanged: () => {},
    onExportReady: multipleChartsExport.exportCallbacks[0].onExportReady,
    defaultBrandBubbleForCollection: selectedBrandBubble,
  };

  if (isLoading) return renderLoading();

  return <OpportunityReviewBubbleChart {...props} />;
};

const BubbleChartInsight: FC<BubbleChartInsightRenderer> = (props) => {
  const baseApiParams = {
    clientId: props.insight.data.apiParams.clientId,
    productType: props.insight.data.apiParams.productType,
    periodType: props.insight.data.apiParams.periodType,
    period: props.insight.data.apiParams.period,
    comparisonPeriod: props.insight.data.apiParams.comparisonPeriod,
    countries: props.insight.data.apiParams.country,
    productGroup: props.insight.data.apiParams.productGroup,
    appliedFilters: props.insight.data.apiParams.appliedFilters,
  };
  return (
    <Provider store={Store}>
      <ExportContextProvider>
        <PgWarningBanner baseApiParams={baseApiParams} collectionDataType={props.insight.dataType}>
          <BubbleChartComponent {...props} />
        </PgWarningBanner>
      </ExportContextProvider>
    </Provider>
  );
};

export default BubbleChartInsight;
